import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import sortBy from "lodash/sortBy";
import size from "lodash/size";
import map from "lodash/map";
import endsWith from "lodash/endsWith";
import toLower from "lodash/toLower";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import VisibilityIcon from "@mui/icons-material/Visibility";

const RelatedDocumentsList = ({
  relatedDocuments,
  docsOrder,
  setOrder,
  isDraft,
  previewPdf,
}) => {
  const [documents, setDocuments] = useState(
    relatedDocuments.map((doc) => ({ ...doc, checked: true }))
  );
  const draftCheckedRef = useRef(false);

  useEffect(() => {
    if (draftCheckedRef.current) {
      setDocuments(relatedDocuments.map((doc) => ({ ...doc, checked: true })));
    }
  }, [relatedDocuments]);

  useEffect(() => {
    if (isDraft && size(relatedDocuments) > 0) {
      const allDocs = map(relatedDocuments, (doc) => ({
        ...doc,
        checked: docsOrder.includes(doc.id),
      }));
      setDocuments(
        sortBy(allDocs, (doc) => {
          const index = docsOrder.indexOf(doc.id);
          return index === -1 ? Infinity : index;
        })
      );
    }
    draftCheckedRef.current = true;
  }, []);

  useEffect(() => {
    setOrder(
      documents.map((doc) => (doc.checked ? doc.id : null)).filter(Boolean)
    );
  }, [documents]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = documents.findIndex((doc) => doc.id === active.id);
      const newIndex = documents.findIndex((doc) => doc.id === over.id);

      const orderedDocuments = arrayMove(documents, oldIndex, newIndex);
      setDocuments(orderedDocuments);
      setOrder(orderedDocuments.map((doc) => doc.id));
    }
  };

  const toggleCheck = (id) =>
    setDocuments((prevDocuments) => {
      return prevDocuments.map((doc) =>
        doc.id === id ? { ...doc, checked: !doc.checked } : doc
      );
    });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={documents.map((doc) => doc.id)}
        strategy={verticalListSortingStrategy}
      >
        {documents.map((doc) => (
          <SortableDocumentItem
            key={doc.id}
            document={doc}
            toggleCheck={toggleCheck}
            previewPdf={previewPdf}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

function SortableDocumentItem({ document, toggleCheck, previewPdf }) {
  const { id, filename, effectiveDate, checked } = document;
  const isPdf = endsWith(toLower(filename), ".pdf");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: "12px",
    marginBottom: "8px",
    boxShadow: "0px 0px 6px 0px #0000000D",
    backgroundColor: isDragging ? "#f0f0f0" : "#fff",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "6px",
  };

  return (
    <Box ref={setNodeRef} sx={style}>
      <IconButton
        disableRipple
        {...listeners}
        {...attributes}
        sx={{
          cursor: "grab",
          padding: "2px 0",
          background: "#ddd",
          borderRadius: "4px",
        }}
      >
        <DragIndicatorIcon />
      </IconButton>
      <Box flexGrow={1}>
        <Typography fontWeight={700} fontSize={13}>
          {filename}
        </Typography>
        <Typography
          fontWeight={400}
          fontSize={12}
          color="var(--light-grey-text)"
        >
          {new Date(effectiveDate).toLocaleDateString()}
        </Typography>
      </Box>
      <Stack flexDirection="row" alignItems="center" gap={1}>
        {isPdf && (
          <IconButton
            edge="start"
            aria-label="close"
            onClick={() => previewPdf(document)}
          >
            <VisibilityIcon />
          </IconButton>
        )}
        <Checkbox
          onChange={() => toggleCheck(id)}
          checked={checked}
          sx={{
            "&.Mui-checked": {
              color: "var(--red-color)",
            },
          }}
        />
      </Stack>
    </Box>
  );
}

export default RelatedDocumentsList;
